// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { array, bool, object, func, string } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import {
  Button,
  Loading,
  SkeletonPlaceholder,
  SkeletonText,
} from '@carbon/react';
import { Copy, RefreshCw } from 'lucide-react';

import copyToClipboard from 'Utils/copyToClipboard';
import useAnalytics from 'Hooks/useAnalytics';
import useToastNotifications from 'Hooks/useToastNotifications';
import { AnalyticsLink, NWNotification, NWTextInput } from 'Components/shared';

import XlsIcon from 'Assets/images/xls.svg';
import CsvIcon from 'Assets/images/csv.svg';
import PdfIcon from 'Assets/images/pdf.svg';
import HtmlIcon from 'Assets/images/html.svg';
import JsonIcon from 'Assets/images/json.svg';
import FileIcon from 'Assets/images/file.svg';

const StyledAPIInformation = styled.div`
  section {
    margin-bottom: 16px;
    border: 1px solid #e2e2e2;

    h4 {
      font-size: 16px;
      padding: 16px;
      background: #f5f7f9;
    }

    .sectionBody {
      .sectionContent {
        padding: 16px;

        .apiCredentials {
          display: flex;
          line-height: 30px;

          > div {
            display: flex;
            align-items: center;

            .apiInfo {
              display: flex;

              span {
                margin-left: 8px;
              }
            }

            svg {
              cursor: pointer;
              margin-left: 8px;
              color: ${ props => props.theme.colors.altOrange };
            }
          }
        }

        .supportEmail {
          display: flex;
          gap: 16px;
          align-items: center;

          > div {
            flex: none;
          }

          .cds--form-item {
            margin-bottom: 0;
          }

          input {
            min-width: 300px;
          }

          button {
            width: 150px;
            height: 40px;
            padding: 0;
            margin-top: 15px;
            justify-content: center;
            background-color: #da1e28;
            border: 1px solid transparent;
            color: #fff;
          }
        }

        .resetSection {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e2e2e2;

          .resetButton {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px;
            
            svg {
              width: 16px;
              height: 16px;
            }
          }

          .daysRemaining {
            font-size: 14px;
            font-weight: 500;
            padding: 4px 12px;
            border-radius: 4px;
            color: #3a3a3a;

            &.urgent {
              background-color: #FFF2F1;
            }

            &.warning {
              background-color: #F4D9C0;
            }

            &.safe {
              background-color: #F4F9F4;
            }
          }
        }

        p svg {
          cursor: pointer;
          margin-left: 8px;
        }
      }
    }

    .file {
      display: flex;
      align-items: center;
      padding: 16px;
      border-bottom: 1px solid #e2e2e2;

      .fileIcon {
        margin-right: 16px;

        svg {
          width: 40px;
          height: 50px;
        }
      }

      .fileName {
        &.skeleton {
          flex: 1;
        }

        .fileTitle {
          button {
            background: transparent;
            border: none;
            padding: 0;
            font-size: 16px;
            color: #3a3a3a;
            cursor: pointer;
          }

          a {
            word-break: break-all;
            color: #3a3a3a;
          }
        }

        .fileDate {
          margin-top: 4px;

          span {
            color: #707070;
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    section {
      .feedSection {
        flex-direction: column;
        align-items: start;
      }

      .sectionBody .sectionContent {
        .supportEmail {
          gap: 0;
          flex-direction: column;

          > div {
            width: 100%;
          }

          button {
            width: 100%;
            max-width: none;
            margin-top: 0;
          }
        }

        .apiCredentials {
          display: flex;
          flex-direction: column;

          > div {
            .apiInfo {
              width: 90%;
              position: relative;
    
              &:before {
                content: '&nbsp;';
                visibility: hidden;
              }
    
              span {
                position: absolute;
                left: 0;
                right: 0;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                margin-left: 0;
              }
            }
          }

        }

        .resetSection {
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        }
      }

      .file .fileName .fileTitle button {
        text-align: left;
        word-break: break-all;
      }
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 3rem;
  width: 3rem;
`;

const fields = {
  technicalSupportEmail: 'technicalSupportEmail'
};

const APIInformation = ({
  apiInformation,
  loading,
  handleDownload,
  documentationFiles,
  developerTools,
  handleSubmit,
  isRequestPending,
  validators,
  register,
  rhfErrors,
  isAdmin,
  isImpersonated,
  handleResetCredentials,
  isResetPending,
  nextResetDate,
}) => {
  const theme = useTheme();
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const daysRemaining = useMemo(() => {
    if (!nextResetDate) return null;
    const today = new Date();
    const resetDate = new Date(nextResetDate);
    const diffTime = resetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, [nextResetDate]);

  const getDaysRemainingClass = days => {
    if (days === null) return '';
    if (days <= 7) return 'urgent';
    if (days <= 30) return 'warning';
    return 'safe';
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);
    analytics.track('copy_api_information');

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  const renderFileIcon = type => {
    switch (type) {
      case 'xls':
        return <XlsIcon />;
      case 'csv':
        return <CsvIcon />;
      case 'pdf':
        return <PdfIcon />;
      case 'html':
        return <HtmlIcon />;
      case 'json':
        return <JsonIcon />;
      default:
        return <FileIcon />;
    }
  };

  return (
    <StyledAPIInformation theme={ theme }>
      { loading ? Array.from({ length: 2 }, () => (
        <section key={ uniqueId('section') }>
          <h4><SkeletonText /></h4>
          { Array.from({ length: 3 }, () => (
            <div className="file" key={ uniqueId('apiInformation') }>
              <div className="fileIcon">
                <StyledSkeletonPlaceholder />
              </div>
              <div className="fileName skeleton">
                <SkeletonText paragraph lineCount={ 2 } />
              </div>
            </div>
          ))}
        </section>
      )) : (
        <>
          <section>
            <h4>{ t('profile.apiCredentials') }</h4>
            <div className="sectionBody">
              <div className="sectionContent">
                { apiInformation?.technicalSupportEmail ? (
                  <>
                    <NWNotification
                      errors={ [
                        { type: 'success', message: t('profile.apiCredentialsMessage') },
                      ] }
                    />
                    <div className="apiCredentials">
                      { t('profile.apiUser') }:
                      <div>
                        <span className="apiInfo">
                          <span>{ apiInformation?.apiUser }</span>
                        </span>
                        <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(apiInformation?.apiUser) } />
                      </div>
                    </div>
                    <div className="apiCredentials">
                      { t('profile.apiKey') }:
                      <div>
                        <span className="apiInfo">
                          <span>{ apiInformation?.apiKey }</span>
                        </span>
                        <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(apiInformation?.apiKey) } />
                      </div>
                    </div>
                    { isAdmin && (
                      <div className="resetSection">
                        <Button
                          kind="tertiary"
                          size="sm"
                          className="resetButton"
                          onClick={ handleResetCredentials }
                          disabled={ isImpersonated || isResetPending }
                        >
                          { isResetPending && <Loading active={ isResetPending } small withOverlay={ false } /> }
                          <RefreshCw />
                          { t('profile.resetApiCredentials') }
                        </Button>
                        { daysRemaining !== null && (
                          <span className={ `daysRemaining ${ getDaysRemainingClass(daysRemaining) }` }>
                            { t('profile.daysUntilAutoReset', { days: daysRemaining }) }
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <NWNotification errors={ [{ type: 'error', message: t('profile.apiCredentialsError') }] } />
                    <div className="supportEmail">
                      <NWTextInput
                        light
                        required
                        labelText={ t(`pages.requestForm.${ fields.technicalSupportEmail }`) }
                        { ...sharedProps(fields.technicalSupportEmail) }
                      />
                      <Button
                        onClick={ handleSubmit }
                        className="danger"
                        kind="danger"
                        disabled={ isRequestPending }
                      >
                        { isRequestPending && <Loading active={ isRequestPending } small withOverlay={ false } /> }
                        { t('pages.save') }
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
          <section>
            <h4>{ t('profile.NODAPIDocumentation') }</h4>
            <div className="sectionBody">
              { documentationFiles.map(item => (
                <div className="file" key={ item?.uploadedInformationId }>
                  <div className="fileIcon">
                    { renderFileIcon(item?.type) }
                  </div>
                  <div className="fileName">
                    <div className="fileTitle">
                      { item?.fileName ? (
                        <button
                          type="button"
                          onClick={ () => handleDownload(item?.url) }
                        >
                          { item?.fileName || item?.linkName }
                        </button>
                      ) : (
                        <AnalyticsLink
                          to={ item?.linkUrl }
                          target="_blank"
                          event={ { action: 'linked_clicked' } }
                        >
                          { item?.linkName }
                        </AnalyticsLink>
                      ) }
                    </div>
                    <div className="fileDate"><span>{ t('profile.updatedAt') }: </span>{ item?.updatedAt }</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h4>{ t('profile.developerTools') }</h4>
            <div className="sectionBody">
              { developerTools.map(item => (
                <div className="file" key={ item?.uploadedInformationId }>
                  <div className="fileIcon">
                    { renderFileIcon(item?.type) }
                  </div>
                  <div className="fileName">
                    <div className="fileTitle">
                      { item?.fileName ? (
                        <button
                          type="button"
                          onClick={ () => handleDownload(item?.url) }
                        >
                          { item?.fileName || item?.linkName }
                        </button>
                      ) : (
                        <AnalyticsLink
                          to={ item?.linkUrl }
                          target="_blank"
                          event={ { action: 'linked_clicked' } }
                        >
                          { item?.linkName }
                        </AnalyticsLink>
                      ) }
                    </div>
                    <div className="fileDate"><span>{ t('profile.updatedAt') }: </span>{ item?.updatedAt }</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </StyledAPIInformation>
  );
};

APIInformation.propTypes = {
  apiInformation: object,
  loading: bool,
  handleDownload: func,
  documentationFiles: array,
  developerTools: array,
  handleSubmit: func,
  isRequestPending: bool,
  validators: object,
  register: func,
  rhfErrors: object,
  isAdmin: bool,
  isImpersonated: bool,
  handleResetCredentials: func,
  isResetPending: bool,
  nextResetDate: string,
};

APIInformation.displayName = 'APIInformationComponent';

export default APIInformation;
